import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatPrice } from "@/lib/api";
import { Volume2, VolumeX, Heart, ArrowRight, Play, ChevronUp, ChevronDown } from "lucide-react";

export default function Reels() {
    const [reels, setReels] = useState([]);
    const [products, setProducts] = useState({});
    const [activeIdx, setActiveIdx] = useState(0);
    const [muted, setMuted] = useState(true);
    const containerRef = useRef(null);
    const itemRefs = useRef([]);

    useEffect(() => {
        api.get("/content/reels").then(async (res) => {
            setReels(res.data);
            // load products in parallel
            const slugs = res.data.map(r => r.product_slug).filter(Boolean);
            const results = await Promise.all(slugs.map(s => api.get(`/catalog/products/${s}`).then(r => r.data.product).catch(() => null)));
            const map = {};
            results.forEach((p, i) => { if (p) map[slugs[i]] = p; });
            setProducts(map);
        });
    }, []);

    useEffect(() => {
        if (itemRefs.current.length === 0) return;
        const io = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                const idx = Number(e.target.dataset.idx);
                const v = e.target.querySelector("video");
                if (!v) return;
                if (e.isIntersecting && e.intersectionRatio > 0.6) {
                    setActiveIdx(idx);
                    v.play().catch(() => {});
                } else {
                    v.pause();
                }
            });
        }, { threshold: [0, 0.6, 1] });
        itemRefs.current.forEach(el => el && io.observe(el));
        return () => io.disconnect();
    }, [reels]);

    const scrollToIdx = (idx) => {
        if (itemRefs.current[idx]) {
            itemRefs.current[idx].scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleLike = async (id) => {
        const r = await api.post(`/content/reels/${id}/like`);
        setReels(prev => prev.map(x => x.id === id ? r.data : x));
    };

    return (
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-10" data-testid="reels-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <p className="font-mono-ui text-[#857C72]">X-Plosiv Stories</p>
                    <h1 className="font-display text-4xl md:text-5xl mt-1">Events in motion.</h1>
                </div>
                <button onClick={() => setMuted(m => !m)} className="hidden sm:flex h-10 px-4 rounded-full bg-white border border-[#E7DED2] items-center gap-2 text-sm" data-testid="reels-page-mute">
                    {muted ? <><VolumeX className="h-4 w-4" /> Unmute</> : <><Volume2 className="h-4 w-4" /> Mute</>}
                </button>
            </div>

            <div className="grid lg:grid-cols-[420px_1fr] gap-6" data-testid="reels-player">
                <div className="relative">
                    <div ref={containerRef} className="dw-reels-scroller relative rounded-[28px] overflow-hidden border border-[#E7DED2] bg-[#121214] h-[78vh] sm:h-[80vh] overflow-y-auto">
                        {reels.map((r, idx) => (
                            <div
                                key={r.id}
                                ref={el => itemRefs.current[idx] = el}
                                data-idx={idx}
                                className="dw-reels-item relative h-[78vh] sm:h-[80vh] snap-start"
                            >
                                <video
                                    src={r.video_url} poster={r.poster}
                                    muted={muted} loop playsInline preload="metadata"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex flex-col gap-3">
                                    <button onClick={() => handleLike(r.id)} className="h-10 w-10 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center text-white" data-testid="reels-like-button">
                                        <Heart className="h-4 w-4" />
                                    </button>
                                    <span className="text-[10px] text-white/80 text-center">{r.likes}</span>
                                </div>
                                <div className="absolute inset-x-3 bottom-4">
                                    <p className="text-white/90 text-xs font-mono-ui mb-2">{r.title}</p>
                                    {products[r.product_slug] && (
                                        <Link to={`/product/${products[r.product_slug].slug}`} className="flex items-center gap-3 bg-white/12 backdrop-blur-xl border border-white/25 rounded-[18px] p-2.5 hover:bg-white/20 transition-colors" data-testid="reels-shop-look-button">
                                            <img src={products[r.product_slug].images?.[0]} alt="" className="h-12 w-12 rounded-xl object-cover" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-white truncate">{products[r.product_slug].title}</p>
                                                <p className="text-xs text-white font-semibold mt-0.5">{formatPrice(products[r.product_slug].price)}</p>
                                            </div>
                                            <span className="px-3 h-8 rounded-full bg-white text-black text-[11px] font-mono-ui flex items-center gap-1">Shop <ArrowRight className="h-3 w-3" /></span>
                                        </Link>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* up/down */}
                    <div className="absolute right-[-50px] top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-2">
                        <button onClick={() => scrollToIdx(Math.max(0, activeIdx - 1))} className="h-10 w-10 rounded-full bg-white border border-[#E7DED2] flex items-center justify-center hover:bg-[#FFFCF7]"><ChevronUp className="h-4 w-4" /></button>
                        <button onClick={() => scrollToIdx(Math.min(reels.length - 1, activeIdx + 1))} className="h-10 w-10 rounded-full bg-white border border-[#E7DED2] flex items-center justify-center hover:bg-[#FFFCF7]"><ChevronDown className="h-4 w-4" /></button>
                    </div>
                </div>

                <div className="hidden lg:block">
                    <div className="dw-section p-6 sticky top-32">
                        <h3 className="font-display text-2xl mb-4">Now playing</h3>
                        {reels[activeIdx] && products[reels[activeIdx].product_slug] ? (
                            <div>
                                <Link to={`/product/${products[reels[activeIdx].product_slug].slug}`}>
                                    <img src={products[reels[activeIdx].product_slug].images?.[0]} alt="" className="aspect-[4/5] w-full object-cover rounded-2xl" />
                                </Link>
                                <p className="font-medium mt-4">{products[reels[activeIdx].product_slug].title}</p>
                                <p className="text-sm text-muted-foreground mt-1">{products[reels[activeIdx].product_slug].category}</p>
                                <p className="font-display text-2xl mt-2">{formatPrice(products[reels[activeIdx].product_slug].price)}</p>
                                <Link to={`/product/${products[reels[activeIdx].product_slug].slug}`} className="mt-5 dw-btn-primary px-6 py-3 font-mono-ui inline-flex w-full justify-center">Shop the look</Link>
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">Loading look…</p>
                        )}
                    </div>
                </div>
            </div>
            <div className="h-24" />
        </div>
    );
}
